import { z } from 'zod'

import { NAMED_MODBUS_CLIENT_NAME_PATTERN } from '@/features/controller/configuration/models/raspiProperties/namedModbusClients'
import {
  ModbusDataType,
  ModbusPowerRegisterVariant,
  ModbusRegisterType,
  ModbusSerialEncoding,
  SerialPort,
  SerialPortParity,
  WordOrder,
} from '@/features/controller/configuration/models/raspiProperties/types'

/** The fields `SerialPortConfigFields` renders, so both drawers validate what that component edits. */
const getSerialPortConfigSchema = () =>
  z.object({
    baudRate: z.int('Baud rate is required').positive('Baud rate must be positive'),
    // Fixed and disabled in the UI, so only a hand-edited file can be off; readSerialMode reports
    // that where the user can see it, and refusing the save here would leave them no way to fix it
    dataBits: z.int(),
    parity: z.enum(SerialPortParity),
    stopBits: z.int().min(1).max(2),
  })

export const getLegacySerialPortSchema = () =>
  z.object({
    legacySerialPortConfig: z.object({
      portConfig: getSerialPortConfigSchema(),
      serialPort: z.enum(SerialPort),
    }),
    legacyModbusSerialConfig: z.object({
      deviceNumber: z.int('Device number is required').positive('Device number must be positive'),
      encoding: z.enum(ModbusSerialEncoding),
      transmissionDelayInMilliseconds: z.int().nonnegative('Delay cannot be negative').optional(),
    }),
  })

/** A TCP port is a 16-bit number, so no socket can be opened on a higher one. */
const MAX_TCP_PORT = 65535

const getTcpPortSchema = () =>
  z
    .int()
    .positive({ message: 'Port must be a positive number' })
    .max(MAX_TCP_PORT, { message: `Port cannot be above ${MAX_TCP_PORT}` })

export const getLegacyModbusTcpSchema = () =>
  z.object({
    host: z.string().nonempty({ message: 'Host is required' }),
    port: getTcpPortSchema(),
  })

const getNamedModbusClientNameSchema = (takenNames: string[]) =>
  z
    .string()
    .nonempty({ message: 'Name is required' })
    .regex(NAMED_MODBUS_CLIENT_NAME_PATTERN, {
      message: 'Use letters, digits, dashes and underscores only, starting with a letter or digit',
    })
    .refine((name) => !takenNames.includes(name), { message: 'Another connection already uses this name' })

/**
 * @param takenNames names already used by another connection, which this one cannot reuse
 */
export const getNamedModbusTcpSchema = (takenNames: string[] = []) =>
  z.object({
    address: z.string().nonempty({ message: 'Host name or IP address is required' }),
    connectTimeoutMs: z.int().positive('Connect timeout must be positive').optional(),
    name: getNamedModbusClientNameSchema(takenNames),
    port: getTcpPortSchema(),
    responseTimeoutMs: z.int().positive('Response timeout must be positive').optional(),
    retries: z.int().nonnegative('Retries cannot be negative').optional(),
  })

/**
 * @param takenNames names already used by another connection, which this one cannot reuse
 */
export const getNamedModbusSerialSchema = (takenNames: string[] = []) =>
  z.object({
    name: getNamedModbusClientNameSchema(takenNames),
    portConfig: getSerialPortConfigSchema(),
    responseTimeoutMs: z.int().positive('Response timeout must be positive').optional(),
    retries: z.int().nonnegative('Retries cannot be negative').optional(),
    serialPort: z.enum(SerialPort),
  })

export const getLegacyModbusMeterRegisterSchema = () => {
  return z
    .object({
      address: z.int('Register address is required').nonnegative('Address cannot be negative'),
      registers: z.enum(ModbusRegisterType, 'Choose holding or input registers'),
    })
    .optional()
}

export const getLegacyModbusMeterSchema = () =>
  z.object({
    activePowerRegisters: z
      .object({
        address: z.int('Active power register is required').nonnegative('Address cannot be negative'),
        registers: z.enum(ModbusRegisterType, 'Choose holding or input registers'),
        variant: z.enum(ModbusPowerRegisterVariant, 'Choose 3-phase or total active power registers'),
      })
      .optional()
      .refine((schema) => schema !== undefined, { message: 'Active power registers are required', path: ['address'] }),
    activePowerScale: z
      .number()
      .optional()
      .refine((value) => value !== 0, 'Scale cannot be zero'),
    dataTimestampRegister: getLegacyModbusMeterRegisterSchema(),
    deviceNumber: z.int('Device number is required').positive('Device number must be positive'),
    meterErrorRegister: getLegacyModbusMeterRegisterSchema(),
    powerMeasurementSkipPeriodInMilliseconds: z
      .int('Period between measurements is required')
      .positive('Period between measurements must be positive'),
    powerRegistersDataType: z.enum(ModbusDataType),
    reactivePowerRegister: getLegacyModbusMeterRegisterSchema(),
    reactivePowerScale: z
      .number()
      .optional()
      .refine((value) => value !== 0, 'Scale cannot be zero'),
    registerAddressOffset: z.int().optional(),
    systemTimestampRegister: getLegacyModbusMeterRegisterSchema(),
    unitId: z.int('Unit id is required').positive('Unit id must be positive'),
    watchdogRegister: getLegacyModbusMeterRegisterSchema(),
    wordOrder: z.enum(WordOrder),
  })
